type Callback<D = unknown> = (data?: D) => void;
type HashKey = string;
export interface BasePubSubEvents {}

export const randomHashKeyGenerator = (): HashKey => {
  return Math.random().toString(36).substr(2, 9);
};

const matchesWildcard = (pattern: string, eventName: string): boolean => {
  if (!pattern.includes("*")) return pattern === eventName;
  const regex = new RegExp("^" + pattern.replace(/\*/g, ".*") + "$");
  return regex.test(eventName);
};

interface Subscribers {
  eventName: string;
  hashKey: HashKey;
  callback: Callback<any>;
}

export class PubSub<TEventMap extends BasePubSubEvents = BasePubSubEvents> {
  private subscribers: Subscribers[];
  private debug: boolean;

  constructor(debug?: boolean) {
    this.subscribers = [];
    this.debug = debug ?? import.meta.env.DEV;

    this.log("INITIALIZED");
  }

  subscribe<E extends keyof TEventMap>(
    event: E,
    callback: TEventMap[E] extends void ? () => void : (data: TEventMap[E]) => void
  ): HashKey;

  subscribe(event: string, callback: (data: any) => void): HashKey;

  subscribe<E extends keyof TEventMap>(
    event: E | string,
    callback: TEventMap[E] extends void ? () => void : (data: TEventMap[E] | any) => void
  ): HashKey {
    const hashKey = randomHashKeyGenerator();

    this.subscribers.push({
      eventName: event as string,
      hashKey,
      callback,
    });

    this.log("SUBSCRIBE", {
      event: event as string,
      hashKey,
      isWildcard: (event as string).includes("*"),
      totalSubscribers: this.subscribers.length,
    });

    // return hashkey for unsubscribe method
    return hashKey;
  }

  once<E extends keyof TEventMap>(
    event: E,
    callback: TEventMap[E] extends void ? () => void : (data: TEventMap[E]) => void
  ): HashKey;

  once(event: string, callback: (data: any) => void): HashKey;

  once<E extends keyof TEventMap>(
    event: E | string,
    callback: TEventMap[E] extends void ? () => void : (data: TEventMap[E] | any) => void
  ): HashKey {
    const hashKey = randomHashKeyGenerator();

    const onceWrapper = (data: any) => {
      callback(data);
      this.unsubscribe(hashKey);
    };

    this.subscribers.push({
      eventName: event as string,
      hashKey,
      callback: onceWrapper,
    });

    this.log("ONCE", {
      event: event as string,
      hashKey,
      isWildcard: (event as string).includes("*"),
      totalSubscribers: this.subscribers.length,
    });

    return hashKey;
  }

  unsubscribe = (hashKey: HashKey): boolean => {
    if (!hashKey) return false;

    const isExists = this.subscribers.find(({ hashKey: subHashKey }) => subHashKey === hashKey);

    this.log("UNSUBSCRIBE", {
      hashKey,
      found: !!isExists,
      eventName: isExists?.eventName,
      totalSubscribersBeforeRemoval: this.subscribers.length,
    });

    if (!isExists) return false;

    const events = this.subscribers.filter((subscriber) => subscriber.hashKey !== hashKey);
    this.subscribers = events;

    return true;
  };

  publish = <E extends keyof TEventMap>(
    event: E,
    ...args: TEventMap[E] extends void ? [] : [data: TEventMap[E]]
  ): boolean => {
    const exactMatches = this.subscribers.filter(({ eventName }) => eventName === event);

    const wildcardMatches = this.subscribers.filter(
      ({ eventName }) => eventName !== event && matchesWildcard(eventName, event as string)
    );

    const allMatches = [...exactMatches, ...wildcardMatches];

    const [data] = args;

    this.log("PUBLISH", {
      event: event as string,
      data,
      exactMatches: exactMatches.length,
      wildcardMatches: wildcardMatches.length,
      totalMatches: allMatches.length,
      matchedSubscribers: allMatches.map((sub) => sub.hashKey),
    });

    if (allMatches.length === 0) return false;

    allMatches.forEach((event) => {
      event.callback(data);
    });

    return true;
  };

  getAllSubscribers = (): Subscribers[] => this.subscribers;

  private log(operation: string, details?: unknown): void {
    if (!this.debug) return;

    const timestamp = new Date().toISOString().split("T")[1].split(".")[0];
    console.group(`%c[PubSub] [${timestamp}] ${operation}`, "color: #4CAF50; font-weight: bold");
    if (details) {
      console.log(details);
    }
    console.groupEnd();
  }
}
